import React, { useState, useEffect } from 'react';
import {
  TableContainer,
  TableRow,
  TableCell,
  TableSortLabel,
  Button,
  Stack,
} from '@mui/material';
import { StyledTable, StyledTableHead, StyledTableBody } from './Table.styled';
import TextView from '../../TextView';
import { ASSETS } from '../../../utils/config';
import '../../../App.css';
import { colors } from '../../../utils';
import { CircularProgress } from '@mui/material';
import { getMediaByCategory, getWheelMedia } from '../../../services/api';
import { useDispatch, useSelector } from 'react-redux';
import {
  setVideos,
  setAudios,
  setDocuments,
  setImages,
  setWheels,
} from '../../../redux/reducers/storage';
import PurpleDoc from '../../../assets/images/PurpleDoc.svg';
import { fileIcons } from '../../../constants/documentIcon';

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 bytes';
  const k = 1024;
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const MediaTable = ({ type, wheelId }) => {
  const [selectedRows, setSelectedRows] = useState({
    images: [],
    videos: [],
    documents: [],
    audios: [],
    wheels: [],
  });
  const [order, setOrder] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('name');
  const [paths, setPaths] = useState({
    images: [],
    videos: [],
    documents: [],
    audios: [],
    wheels: [],
  });
  const dispatch = useDispatch();

  const data = useSelector((state) => {
    switch (type) {
      case 'images':
        return state.storage.images ?? [];
      case 'videos':
        return state.storage.videos ?? [];
      case 'documents':
        return state.storage.documents ?? [];
      case 'audios':
        return state.storage.audios ?? [];
      case 'wheels':
        return state.storage.wheels ?? [];
      default:
        return [];
    }
  });

  const updateMediaData = (mediaData) => {
    // Transform the API response data into the expected format for each media type
    const transformedData = {
      images: Object.entries(mediaData.images || {}).map(([url, details]) => ({
        name: url.split('/').pop(),
        size: details.size,
        size_display: details.size_display || formatBytes(details.size),
        path: details.path,
        extension: details.extension,
        url: url
      })),
      videos: Object.entries(mediaData.video || {}).map(([url, details]) => ({
        name: url.split('/').pop(),
        size: details.size,
        size_display: details.size_display || formatBytes(details.size),
        path: details.path,
        extension: details.extension,
        url: url
      })),
      documents: Object.entries(mediaData.document || {}).map(([url, details]) => ({
        name: url.split('/').pop(),
        size: details.size,
        size_display: details.size_display || formatBytes(details.size),
        path: details.path,
        extension: details.extension,
        url: url
      })),
      audios: Object.entries(mediaData.audio || {}).map(([url, details]) => ({
        name: url.split('/').pop(),
        size: details.size,
        size_display: details.size_display || formatBytes(details.size),
        path: details.path,
        extension: details.extension,
        url: url
      }))
    };

    // Update all media types in Redux at once
    dispatch(setImages(transformedData.images));
    dispatch(setVideos(transformedData.videos));
    dispatch(setDocuments(transformedData.documents));
    dispatch(setAudios(transformedData.audios));
  };

  const fetchTypeMedia = async () => {
    try {
      setLoading(true);
      const response = await getWheelMedia(wheelId);

      // Check if response.data.data.data.data is an empty object
      if (response?.data?.data?.data?.data && Object.keys(response.data.data.data.data).length === 0) {
        // Set empty arrays for all media types
        dispatch(setImages([]));
        dispatch(setVideos([]));
        dispatch(setDocuments([]));
        dispatch(setAudios([]));
      } else {
        // Handle normal response
        updateMediaData(response.data.data.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch Type Media:', err);
      // Set empty arrays on error as well
      dispatch(setImages([]));
      dispatch(setVideos([]));
      dispatch(setDocuments([]));
      dispatch(setAudios([]));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchTypeMedia();
      setLoading(false);
    };

    fetchData();
  }, []);

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort the data based on order
  const sortedData = React.useMemo(() => {
    if (!data.length) return []; // Return an empty array if data is empty

    return [...data].sort((a, b) => {
      if (!a[orderBy] || !b[orderBy]) return 0; // Skip if the field is missing
      if (orderBy === 'size') {
        const sizeA = parseFloat(a[orderBy]);
        const sizeB = parseFloat(b[orderBy]);
        return order === 'asc' ? sizeA - sizeB : sizeB - sizeA;
      } else {
        if (order === 'asc') {
          return a[orderBy] < b[orderBy] ? -1 : 1;
        } else {
          return a[orderBy] > b[orderBy] ? -1 : 1;
        }
      }
    });
  }, [data, order, orderBy]);

  // Helper: choose icon based on file type
  const getIcon = (type, url, item) => {
    console.log(item, "item")
    switch (type) {
      case 'images':
        return (
          <img
            alt="img"
            src={url}
            style={{
              marginRight: '10px',
              height: '34px',
              borderRadius: '5px',
              width: '34px',
              objectFit: 'cover',
            }}
          />
        );
      case 'audios':
        return (
          <img
            src={ASSETS.StorageAudio}
            alt="doc"
            style={{ marginRight: '10px', height: '35px', width: '35px' }}
          />
        );
      case 'videos':
        return (
          <video
            width="35"
            height="35"
            preload="metadata"
            style={{ objectFit: 'cover', borderRadius: '5px', marginRight: '10px' }}
          >
            <source src={url} type="video/mp4" />
          </video>
        );
      case 'documents':
        return (
          <img
            src={fileIcons[item?.extension?.replace('.', '')]}
            alt="doc"
            style={{ marginRight: '10px', height: '35px', width: '35px' }}
          />
        );
      default:
        return (
          <img
            alt="default"
            src={ASSETS.wheel_img}
            style={{ marginRight: '10px', height: '35px', width: '35px' }}
          />
        );
    }
  };

  if (!sortedData.length) {
    return (
      <Stack display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} my={10}>
        <img src={PurpleDoc} alt="purpleDoc" style={{ height: '50px', width: '50px', marginBottom: '5px' }} />
        <TextView
          style={{
            fontSize: '20px',
            fontWeight: '700',
            textAlign: 'center',
            color: colors.darkGrey,
          }}
        >
          No data available
        </TextView>
      </Stack>
    );
  }
  return (
    <>
      <div>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '50vh',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <div>
            <TableContainer>
              <StyledTable>
                <StyledTableHead>
                  <TableRow>

                    <TableCell style={{ padding: '5px' }}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        <TextView
                          variant="subtitle"
                          color="#8F9094"
                          style={{ fontWeight: '400', fontSize: '12px' }}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </TextView>
                      </TableSortLabel>
                    </TableCell>

                    <TableCell style={{ padding: '5px' }}>
                      <TableSortLabel
                        active={orderBy === 'size'}
                        direction={orderBy === 'size' ? order : 'asc'}
                        onClick={() => handleRequestSort('size')}
                      >
                        <TextView
                          variant="subtitle"
                          color="#8F9094"
                          style={{ fontWeight: '400', fontSize: '12px' }}
                        >
                          Size
                        </TextView>
                      </TableSortLabel>
                    </TableCell>

                  </TableRow>
                </StyledTableHead>

                <StyledTableBody>
                  {sortedData.map((item, index) => (
                    <TableRow key={index}>

                      <TableCell style={{ padding: '5px' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          {getIcon(type, item.url, item)}
                          <TextView color="#565556" style={{ fontWeight: '500', fontSize: 12 }}>
                            {type === 'wheels'
                              ? item.wheel_name.length > 30
                                ? `${item.wheel_name.substring(0, 30)}...`
                                : item.wheel_name
                              : item.name.length > 30
                                ? `${item.name.substring(0, 30)}...`
                                : item.name}
                          </TextView>
                        </div>
                      </TableCell>
                      <TableCell style={{ padding: '5px' }}>
                        <TextView color="#565556" style={{ fontWeight: '500', fontSize: '12px' }}>
                          {item.size_display}
                        </TextView>
                      </TableCell>
                    </TableRow>
                  ))}
                </StyledTableBody>
              </StyledTable>
            </TableContainer>
          </div>
        )}
      </div>
    </>
  );
};

export default MediaTable;
